import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Stethoscope, ShieldCheck, HeartHandshake, FileText, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [clockTime, setClockTime] = useState("");

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setClockTime(
        d.toLocaleTimeString("ja-JP", { hour: "2-digit", minute: "2-digit", second: "2-digit" })
      );
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`ようこそ、${u.name}さん`);
      if (u.role === "super_admin") nav("/admin");
      else nav(loc.state?.from?.pathname || "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "ログインに失敗しました");
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (e, p) => { setEmail(e); setPassword(p); };

  const today = new Date().toLocaleDateString("ja-JP", {
    year: "numeric", month: "long", day: "numeric", weekday: "long",
  });

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#FBFBF9]">
      {/* Decorative background grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, #2C5545 1px, transparent 1px), linear-gradient(to bottom, #2C5545 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />
      {/* Soft glow top-right */}
      <div
        className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(217,226,213,0.8) 0%, rgba(217,226,213,0) 70%)",
          filter: "blur(40px)",
        }}
      />
      {/* Soft glow bottom-left */}
      <div
        className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(229,217,197,0.6) 0%, rgba(229,217,197,0) 70%)",
          filter: "blur(40px)",
        }}
      />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 md:px-12 py-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-[#2C5545] flex items-center justify-center text-white shadow-lg shadow-[#2C5545]/20">
              <Stethoscope size={18} strokeWidth={1.5} />
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-[#3A6B52] rounded-full animate-pulse ring-2 ring-[#FBFBF9]" />
          </div>
          <div className="hidden sm:block">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#8A8A86] font-numeric">VNS · Operation Suite</div>
            <div className="text-sm font-bold text-[#1C1C1A]">訪問看護運営システム</div>
          </div>
        </div>
        <div className="hidden md:flex items-center gap-6 text-xs text-[#8A8A86] font-numeric">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#3A6B52] animate-pulse" />
            <span>SYSTEM ONLINE</span>
          </div>
          <div className="tracking-[0.1em]">{clockTime}</div>
        </div>
      </div>

      <div className="relative z-10 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] min-h-[calc(100vh-80px)]">
        {/* LEFT — Hero */}
        <div className="relative flex flex-col justify-between px-6 md:px-16 py-8 md:py-12">
          {/* Editorial headline */}
          <div className="flex-1 flex flex-col justify-center max-w-2xl">
            <div className="flex items-center gap-2 mb-8">
              <div className="h-px w-10 bg-[#2C5545]" />
              <span className="text-xs tracking-[0.3em] uppercase text-[#2C5545] font-numeric font-medium">
                Est. 2026 · 日本語対応
              </span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#1C1C1A] leading-[0.95] mb-8">
              安心を、<br />
              <span className="font-doc italic font-normal text-[#2C5545]">
                ご自宅へ。
              </span>
            </h1>

            <p className="text-base md:text-lg text-[#4A4A48] leading-relaxed max-w-lg mb-10">
              訪問看護の記録・書類・スケジュールを、ひとつに。
              現場の看護師さんと事業所の運営を静かに支える、
              <span className="text-[#2C5545] font-medium">完全無料運用</span>
              のSaaS。
            </p>

            {/* Feature chips */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-10">
              {[
                { icon: FileText, label: "書類41種" },
                { icon: Sparkles, label: "OCR自動入力" },
                { icon: HeartHandshake, label: "PHQ-9対応" },
                { icon: ShieldCheck, label: "テナント分離" },
              ].map((f, i) => (
                <div
                  key={f.label}
                  className="group flex flex-col items-start gap-2 p-3 rounded-xl bg-white/60 backdrop-blur-sm border border-[#E5E5E3] hover:border-[#2C5545] hover:bg-white transition"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <f.icon size={16} className="text-[#2C5545]" strokeWidth={1.5} />
                  <span className="text-xs text-[#1C1C1A] font-medium">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom: Trust footer */}
          <div className="flex items-center justify-between flex-wrap gap-4 pt-8 border-t border-[#E5E5E3]">
            <div className="text-xs text-[#8A8A86] font-numeric tracking-[0.1em]">
              {today}
            </div>
            <div className="flex items-center gap-6 text-xs text-[#8A8A86]">
              <span>マルチテナント</span>
              <span>·</span>
              <span>JWT認証</span>
              <span>·</span>
              <span>日本の介護保険制度準拠</span>
            </div>
          </div>
        </div>

        {/* RIGHT — Login card */}
        <div className="relative flex items-center justify-center px-6 md:px-12 py-8 md:py-12">
          {/* Hero image backdrop with heavy treatment */}
          <div className="absolute inset-0 overflow-hidden lg:block hidden">
            <img
              src="https://images.unsplash.com/photo-1592393532405-fb1f165c4a1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt=""
              className="absolute inset-0 w-full h-full object-cover opacity-15"
              style={{ filter: "grayscale(30%) contrast(1.05)" }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#FBFBF9] via-[#FBFBF9]/60 to-transparent" />
          </div>

          <div className="relative w-full max-w-md">
            {/* Stacked layers for depth */}
            <div className="absolute -inset-4 bg-gradient-to-br from-[#D9E2D5] to-[#E5D9C5] rounded-[28px] opacity-40 blur-2xl" />
            <div className="relative bg-white border border-[#E5E5E3] rounded-2xl shadow-xl shadow-[#2C5545]/5 overflow-hidden">
              {/* Card header strip */}
              <div className="bg-[#2C5545] text-white px-8 py-5 flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase opacity-80 font-numeric">Sign in</div>
                  <div className="text-xl font-bold mt-0.5">ログイン</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-70 font-numeric">Secure · TLS</div>
                  <div className="flex items-center gap-1 mt-1 justify-end">
                    <ShieldCheck size={12} />
                    <span className="text-[10px] font-numeric">256-bit</span>
                  </div>
                </div>
              </div>

              <form onSubmit={submit} className="p-8 space-y-5">
                <div>
                  <Label htmlFor="email" className="text-[10px] tracking-[0.2em] uppercase text-[#4A4A48] font-numeric">
                    メールアドレス
                  </Label>
                  <Input
                    id="email"
                    data-testid="login-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-2 h-12 border-[#E5E5E3] focus-visible:ring-[#2C5545] bg-[#FBFBF9]/50 font-numeric"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[10px] tracking-[0.2em] uppercase text-[#4A4A48] font-numeric">
                    パスワード
                  </Label>
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-2 h-12 border-[#E5E5E3] focus-visible:ring-[#2C5545] bg-[#FBFBF9]/50 font-numeric"
                    placeholder="••••••••"
                  />
                </div>
                <Button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#2C5545] hover:bg-[#1E3D30] text-white group relative overflow-hidden transition-all"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {loading ? (
                      "ログイン中..."
                    ) : (
                      <>
                        ログイン
                        <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                      </>
                    )}
                  </span>
                </Button>
              </form>

              {/* Test accounts */}
              <div className="border-t border-[#E5E5E3] bg-[#FBFBF9]/60 px-8 py-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A86] font-numeric">
                    デモアカウント
                  </div>
                  <div className="text-[10px] text-[#8A8A86] font-numeric">クリックで入力</div>
                </div>
                <div className="space-y-1">
                  {[
                    ["運営者", "superadmin@saas.com", "Super@2026", "#2C5545"],
                    ["管理者", "admin@vns.com", "Admin@2026", "#4A6B8C"],
                    ["看護師", "nurse@vns.com", "Nurse@2026", "#3A6B52"],
                    ["事務", "office@vns.com", "Office@2026", "#D4A373"],
                  ].map(([role, e, p, color]) => (
                    <button
                      key={e}
                      type="button"
                      data-testid={`quick-login-${role}`}
                      onClick={() => quickFill(e, p)}
                      className="w-full group flex items-center gap-3 px-3 py-2 rounded-md hover:bg-white transition"
                    >
                      <span
                        className="w-1 h-6 rounded-full"
                        style={{ background: color }}
                      />
                      <span className="text-sm text-[#1C1C1A] font-medium w-16 text-left">{role}</span>
                      <span className="font-numeric text-xs text-[#8A8A86] truncate flex-1 text-left">{e}</span>
                      <ArrowRight size={12} className="text-[#8A8A86] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Fine print */}
            <div className="mt-6 text-center text-[11px] text-[#8A8A86] font-numeric tracking-wider">
              © 2026 KEISEN · VISITING NURSING STATION SUITE
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
